import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { KeyRound } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";

import {
  AuthAlert,
  AuthField,
  AuthInput,
  AuthLabel,
  AuthSubmitButton,
} from "@/components/auth/auth-primitives";
import { AuthShell } from "@/components/auth/auth-shell";
import { useAuthRedirect } from "@/hooks/use-auth-redirect";
import { authClient } from "@/lib/auth-client";

export const Route = createFileRoute("/(auth)/two-factor")({
  component: TwoFactorPage,
});

/**
 * The three ways past the second factor.
 *
 * `totp` is the intended one. `email` is the rescue when the authenticator is
 * not to hand — weaker, since it inherits the mailbox's security, so it is
 * something the user reaches for rather than the default. `backup` is the last
 * resort, and the only one that still works when the mailbox is gone too.
 */
type Mode = "backup" | "email" | "totp";

/** Long enough to stop rapid re-sends without stranding someone whose mail is slow. */
const RESEND_COOLDOWN_SECONDS = 30;

const COPY: Record<Mode, { hint: string; label: string; placeholder: string }> =
  {
    backup: {
      hint: "Enter one of your saved backup codes.",
      label: "Backup code",
      placeholder: "xxxxxxxx",
    },
    email: {
      hint: "We sent a code to your email address. It expires in a few minutes.",
      label: "Emailed code",
      placeholder: "123456",
    },
    totp: {
      hint: "Enter the 6-digit code from your authenticator app.",
      label: "Authentication code",
      placeholder: "123456",
    },
  };

function TwoFactorPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const redirectTo = useAuthRedirect();
  const [mode, setMode] = useState<Mode>("totp");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }

    const timer = setTimeout(() => {
      setCooldown((seconds) => seconds - 1);
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [cooldown]);

  async function sendEmailCode() {
    setError(null);
    setSending(true);

    const result = await authClient.twoFactor.sendOtp();

    setSending(false);

    if (result.error) {
      setError(
        result.error.message ||
          "We could not send a code. Try your authenticator app or a backup code.",
      );

      return false;
    }

    setCooldown(RESEND_COOLDOWN_SECONDS);
    toast.success("Code sent to your email");

    return true;
  }

  async function switchTo(next: Mode) {
    setCode("");
    setError(null);

    // Sending on arrival rather than behind another button: whoever lands here
    // is already stuck at a prompt, and the extra click buys nothing — the mail
    // only ever goes to their own verified address.
    if (next === "email" && cooldown === 0) {
      setMode(next);
      await sendEmailCode();

      return;
    }

    setMode(next);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    const trimmed = code.trim();
    const result =
      mode === "backup"
        ? await authClient.twoFactor.verifyBackupCode({ code: trimmed })
        : mode === "email"
          ? await authClient.twoFactor.verifyOtp({ code: trimmed })
          : await authClient.twoFactor.verifyTotp({ code: trimmed });

    setSubmitting(false);

    if (result.error) {
      const message =
        result.error.message ||
        result.error.statusText ||
        "That code was not accepted.";
      setError(message);
      return;
    }

    toast.success("Verified");
    if (redirectTo) {
      router.history.replace(redirectTo);
    } else {
      await navigate({ to: "/queue" });
    }
  }

  const copy = COPY[mode];

  return (
    <AuthShell brand={false}>
      <div className="flex flex-col gap-[7px]">
        <h1 className="font-heading text-[clamp(30px,5vw,38px)] leading-[1.06] tracking-[0.01em]">
          Two-factor authentication
        </h1>
        <p className="text-[15px] text-muted-foreground">{copy.hint}</p>
      </div>

      {error ? <AuthAlert>{error}</AuthAlert> : null}

      <form className="flex flex-col gap-[22px]" onSubmit={handleSubmit}>
        <AuthField>
          <AuthLabel htmlFor="two-factor-code">{copy.label}</AuthLabel>
          <AuthInput
            icon={KeyRound}
            id="two-factor-code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            inputMode={mode === "backup" ? "text" : "numeric"}
            autoComplete="one-time-code"
            autoFocus
            placeholder={copy.placeholder}
          />
        </AuthField>

        <AuthSubmitButton
          type="submit"
          disabled={submitting || code.trim().length === 0}
        >
          {submitting ? "Verifying…" : "Verify"}
        </AuthSubmitButton>
      </form>

      <div className="flex flex-col gap-2 text-center text-[14px]">
        {mode === "email" ? (
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground hover:underline disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:no-underline"
            disabled={sending || cooldown > 0}
            onClick={() => {
              void sendEmailCode();
            }}
          >
            {sending
              ? "Sending…"
              : cooldown > 0
                ? `Resend in ${cooldown}s`
                : "Send another code"}
          </button>
        ) : (
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground hover:underline"
            onClick={() => {
              void switchTo("email");
            }}
          >
            Email me a code instead
          </button>
        )}

        {mode === "totp" ? null : (
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground hover:underline"
            onClick={() => {
              void switchTo("totp");
            }}
          >
            Use your authenticator app instead
          </button>
        )}

        {mode === "backup" ? null : (
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground hover:underline"
            onClick={() => {
              void switchTo("backup");
            }}
          >
            Use a backup code instead
          </button>
        )}
      </div>
    </AuthShell>
  );
}
